import { ENGINE_VERSION } from "../constants.js";
import { Rng, clamp } from "../rng.js";
import { humanClubIds } from "../core/access.js";
import { emptyTimeBStats, fillTimeB, fillYouthAcademies } from "../club/youth.js";
import { ensureYouthCoach } from "../club/tecnico-da-base.js";
import { arrumarCamisas } from "../club/camisas.js";
import { renomearJogadores } from "../generator/renomear.js";
import { SCHEMA_VERSION } from "../types.js";
/**
 * Serialização de saves.
 *
 * O mundo já é JSON puro, então salvar é `JSON.stringify`. O que importa aqui é
 * a migração: um save antigo precisa continuar abrindo depois de o motor mudar.
 */
export function serializeWorld(world) {
    return JSON.stringify(world);
}
/**
 * Uma entrada por versão de esquema. A chave é a versão de origem e a função
 * transforma o mundo para a versão seguinte.
 */
export const MIGRATIONS = {
    /** 1 → 2: clubes ganharam ano de fundação e clássicos. */
    1: (world) => {
        for (const club of Object.values(world.clubs)) {
            const legacy = club;
            if (typeof legacy.founded !== "number")
                club.founded = 1920;
            if (!Array.isArray(legacy.rivals))
                club.rivals = [];
        }
    },
    /** 2 → 3: propostas passaram a esperar a resposta do treinador humano. */
    2: (world) => {
        for (const offer of world.offers) {
            if (typeof offer.approved !== "boolean")
                offer.approved = false;
        }
    },
    /**
     * 3 → 4: entrou a competição continental. O save antigo só ganha o campo
     * vazio; a competição em si aparece na virada da temporada seguinte, porque
     * o calendário em andamento não tem dias reservados para ela.
     */
    3: (world) => {
        for (const competition of Object.values(world.competitions)) {
            if (competition.continental === undefined)
                competition.continental = null;
        }
    },
    /**
     * 4 → 5: entraram o empréstimo de jogador e o parcelamento de transferência.
     * O save antigo não tem nenhum dos dois em andamento, então as listas nascem
     * vazias e cada jogador nasce sem empréstimo.
     */
    4: (world) => {
        if (!Array.isArray(world.payments))
            world.payments = [];
        if (!Array.isArray(world.loans))
            world.loans = [];
        if (!world.scouting)
            world.scouting = {};
        if (!world.seasonReports)
            world.seasonReports = {};
        if (!Array.isArray(world.nationalHistory))
            world.nationalHistory = [];
        for (const manager of Object.values(world.managers)) {
            if (!Array.isArray(manager.achievements))
                manager.achievements = [];
            for (const passagem of manager.history) {
                if (!Array.isArray(passagem.titleNames))
                    passagem.titleNames = [];
            }
        }
        for (const player of Object.values(world.players)) {
            if (player.loan === undefined)
                player.loan = null;
        }
        for (const club of Object.values(world.clubs)) {
            if (typeof club.season.scouting !== "number")
                club.season.scouting = 0;
        }
    },
    /**
     * 5 → 6: a convocação para a seleção passou a tirar o jogador do clube.
     *
     * Save antigo nasce com todo mundo disponível, que é o estado correto: a
     * ausência é sempre de poucas rodadas e não faz sentido inventar uma que
     * estava em curso quando o save foi gravado.
     */
    5: (world) => {
        for (const player of Object.values(world.players)) {
            if (typeof player.nationalDutyRounds !== "number")
                player.nationalDutyRounds = 0;
        }
        if (!world.nationalSquads)
            world.nationalSquads = {};
    },
    /**
     * 6 → 7: as categorias de base passaram a existir durante a temporada.
     *
     * A base do save antigo é montada aqui, e não só na próxima pré-temporada,
     * porque uma tela de base vazia durante uma temporada inteira parece defeito.
     * A semente sai do mundo, então dois aparelhos que abrem o mesmo save antigo
     * recebem exatamente os mesmos garotos.
     *
     * O contador `y` é novo e não existia no save antigo, então criá-lo não mexe
     * em nenhum identificador que já tenha sido gerado.
     */
    6: (world) => {
        if (!world.youth)
            world.youth = {};
        fillYouthAcademies(world, new Rng(`${world.seed}|migracao7|${world.season}`));
    },
    /**
     * 7 → 8: a notícia virou chave e parâmetros, e o rótulo do clássico saiu do
     * clube.
     *
     * **A notícia antiga é preservada como veio.** Ela não tem chave nem como
     * ganhar uma: a frase foi montada com nomes, valores e placares que já não
     * estão mais no mundo, e adivinhar de qual molde ela saiu daria erro em
     * notícia parecida. Então ela fica com `key: null` e o texto original, e o
     * cliente a mostra exatamente como estava. Apagar o histórico de alguém para
     * arrumar tradução seria pior que o problema que a tradução resolve.
     *
     * O rótulo da rivalidade, ao contrário, é derivável: `rivalryKey` devolve o
     * mesmo rótulo a partir da intensidade, que continua gravada. Por isso o
     * campo sai do save em vez de ficar guardando português.
     */
    7: (world) => {
        for (const item of world.news) {
            const legacy = item;
            if (item.text)
                continue;
            item.text = {
                key: null,
                title: typeof legacy.title === "string" ? legacy.title : "",
                body: typeof legacy.body === "string" ? legacy.body : "",
            };
            delete legacy.title;
            delete legacy.body;
        }
        for (const club of Object.values(world.clubs)) {
            for (const rival of club.rivals) {
                delete rival.label;
            }
        }
    },
    /**
     * 8 → 9: o que ainda era frase em português dentro do save virou chave.
     *
     * São três lugares, e cada um pede um tratamento diferente:
     *
     * 1. **O objetivo da diretoria** (`board.sportingLabel`) era uma das sete
     *    frases da tabela abaixo. A chave sai da frase, e não da posição
     *    guardada: a mesma posição quer dizer acesso numa divisão e vaga
     *    continental noutra, e recalcular pela liga de hoje mudaria o objetivo
     *    de quem acabou de subir ou cair. Casar a frase preserva exatamente o
     *    que o save dizia, que é o que a migração 7 fez com as notícias.
     * 2. **O resumo do laudo de olheiro** sai fora: a faixa de potencial já está
     *    guardada como número e o veredito passa a ser chave, então a tela
     *    reescreve a frase no idioma de quem lê. O veredito de um laudo antigo é
     *    recuperado pelo fim do resumo, que era uma das três frases da tabela.
     * 3. **As observações do laudo** e o **parecer da diretoria no balanço** não
     *    são deriváveis. A observação antiga fica com `key: null` e o texto
     *    original, exatamente como a notícia antiga; o parecer, que era um
     *    parágrafo montado na hora, é apagado, porque o veredito (`acima`,
     *    `cumpriu`, `abaixo`) mais a posição e os títulos já estão guardados ao
     *    lado e a tela reescreve a frase a partir deles.
     *
     * As frases em português desta tabela são **dado de save antigo**, e não
     * texto que o motor escreva: elas só entram em comparação.
     */
    8: (world) => {
        const objetivos = {
            "Ser campeão": "objetivo.titulo",
            "Brigar pelo título": "objetivo.brigarPeloTitulo",
            "Terminar entre os quatro primeiros": "objetivo.topQuatro",
            "Conseguir o acesso": "objetivo.acesso",
            "Terminar no meio de tabela": "objetivo.meioDeTabela",
            "Meio de tabela": "objetivo.meioDeTabela",
            "Escapar do rebaixamento": "objetivo.fugirDoRebaixamento",
        };
        const chaveDoObjetivo = (antigo) => (typeof antigo === "string" ? objetivos[antigo] : undefined) ?? "objetivo.meioDeTabela";
        for (const club of Object.values(world.clubs)) {
            const legacy = club.board;
            if (club.board.sportingLabelKey === undefined) {
                club.board.sportingLabelKey = chaveDoObjetivo(legacy.sportingLabel);
            }
            delete legacy.sportingLabel;
        }
        const vereditos = [
            ["Tem muito a crescer e vale a aposta.", "olheiro.vaiCrescer"],
            ["Ainda evolui, mas já é quase o que vai ser.", "olheiro.quaseFeito"],
            ["Está no teto dele. O que você vê é o que você leva.", "olheiro.noTeto"],
        ];
        for (const report of Object.values(world.scouting)) {
            const legacy = report;
            if (report.verdictKey === undefined) {
                const antigo = typeof legacy.summary === "string" ? legacy.summary : "";
                report.verdictKey =
                    vereditos.find(([frase]) => antigo.endsWith(frase))?.[1] ?? "olheiro.quaseFeito";
            }
            delete legacy.summary;
            if (Array.isArray(legacy.notes)) {
                report.notes = legacy.notes.map((nota) => typeof nota === "string" ? { key: null, text: nota } : nota);
            }
            else {
                report.notes = [];
            }
        }
        for (const report of Object.values(world.seasonReports)) {
            const legacy = report;
            if (report.targetLabelKey === undefined) {
                report.targetLabelKey = chaveDoObjetivo(legacy.targetLabel);
            }
            delete legacy.targetLabel;
            delete legacy.verdictText;
            for (const offer of report.offers) {
                const antigo = offer;
                if (offer.objectiveKey === undefined) {
                    offer.objectiveKey = chaveDoObjetivo(antigo.objective);
                }
                delete antigo.objective;
            }
        }
    },
    /**
     * 9 → 10: a fase do mata-mata e a conquista de carreira viraram chave.
     *
     * São os dois últimos pedaços de português que o motor gravava, e cada um
     * pede um cuidado diferente:
     *
     * 1. **A fase da copa** é o caso raro em que a frase também era
     *    identificador: `Fixture.stage` guardava "Quartas de final" e
     *    `CupStage.name` guardava a mesma frase, e é por essa igualdade que a
     *    partida encontra a fase a que pertence. Por isso os dois lados são
     *    reescritos na mesma passada, pela mesma tabela: rever um só deixaria a
     *    copa sem conseguir casar jogo com fase, e uma copa em andamento pararia
     *    de avançar sem dar erro nenhum. A tabela é fechada, porque
     *    `buildCupStages` era a única coisa que escrevia esses nomes, e a fase de
     *    tamanho incomum ("Fase de 256") é lida pelo número. O que não for fase
     *    de mata-mata ("liga", "estadual", "Grupo A") passa intacto: aquilo já
     *    era identificador e continua sendo.
     * 2. **A conquista** perde o nome e a descrição, que eram a frase congelada
     *    de quando ela foi ganha. **O `id` não se toca**, e é ele que diz que a
     *    conquista foi ganha: continua em `manager.achievements`, continua no
     *    balanço da temporada em que veio, e o catálogo devolve nome e descrição
     *    a partir dele, agora no idioma de quem está lendo. Uma conquista de dez
     *    temporadas atrás segue na tela de carreira, com a mesma data.
     */
    9: (world) => {
        const fases = {
            "Fase preliminar": "preliminar",
            "Fase inicial": "inicial",
            "Primeira fase": "primeira",
            "Segunda fase": "segunda",
            "Oitavas de final": "oitavas",
            "Quartas de final": "quartas",
            Semifinal: "semifinal",
            Final: "final",
        };
        const chaveDaFase = (antiga) => {
            const conhecida = fases[antiga];
            if (conhecida)
                return conhecida;
            const tamanho = /^Fase de (\d+)$/.exec(antiga);
            return tamanho ? `fase${tamanho[1]}` : antiga;
        };
        for (const fixture of Object.values(world.fixtures)) {
            fixture.stage = chaveDaFase(fixture.stage);
        }
        for (const competition of Object.values(world.competitions)) {
            for (const stage of competition.cup?.stages ?? []) {
                const legacy = stage;
                if (stage.key === undefined && typeof legacy.name === "string") {
                    stage.key = chaveDaFase(legacy.name);
                }
                delete legacy.name;
            }
        }
        for (const report of Object.values(world.seasonReports)) {
            for (const conquista of report.newAchievements ?? []) {
                const legacy = conquista;
                delete legacy.name;
                delete legacy.description;
            }
        }
    },
    10: (world) => {
        // O jornal do mundo nasce vazio. O cursor começa no fim da lista de
        // transferências: o que foi negociado antes da atualização não vira
        // manchete atrasada de uma vez só na primeira rodada.
        const legacy = world;
        legacy.press = {
            headlines: [],
            roundTeams: {},
            transferCursor: world.transfers.length,
            rumors: [],
            coletivas: [],
        };
    },
    11: (world) => {
        // Boatos e coletivas nascem vazios; as manchetes do esquema 11 ficam.
        const press = world.press;
        press.rumors = [];
        press.coletivas = [];
    },
    /**
     * 12 → 13: a varredura dos nomes. Todo jogador e garoto da base do banco do
     * jogo ganha nome pelas regras novas (sobrenomes reais por nacionalidade,
     * apelido único, camisa sem repetir no elenco, nenhum famoso), e o nome
     * antigo é trocado onde ficou escrito.
     */
    12: (world) => {
        renomearJogadores(world);
    },
    /**
     * 13 → 14: a base virou o Time B, e os Livres ganharam origem.
     *
     * Ninguém da base se perde: cada garoto continua onde estava e ganha o que o
     * Time B precisa (qualidade escondida, números zerados, lista de feitos
     * vazia). O clube humano ganha técnico e tem o Time B completado até o
     * tamanho novo, pela mesma peneira de sempre. Os livres de antes não têm
     * como saber quem os dispensou, e ficam com a origem vazia.
     *
     * A semente de tudo sai do mundo e do próprio garoto ou clube, então o mesmo
     * save aberto duas vezes, ou em dois aparelhos, migra igual.
     */
    13: (world) => {
        const legacy = world;
        if (!legacy.timeB)
            legacy.timeB = { tables: {}, lastMatch: {}, history: {}, current: {} };
        for (const prospect of Object.values(world.youth)) {
            const antigo = prospect;
            if (typeof antigo.hidden !== "number") {
                const rng = new Rng(`${world.seed}|migracao14|${prospect.id}`);
                prospect.hidden = Math.round(clamp(rng.gauss(0.97, 0.24), 0.5, 1.6) * 100) / 100;
            }
            if (!antigo.bSeason)
                prospect.bSeason = emptyTimeBStats();
            if (!Array.isArray(antigo.feats))
                prospect.feats = [];
            if (typeof antigo.injuryRounds !== "number")
                prospect.injuryRounds = 0;
        }
        for (const id of world.freeAgents) {
            const player = world.players[id];
            if (player && !player.livre) {
                player.livre = { clubId: null, clubName: null, season: world.season, round: world.round, rounds: 0 };
            }
        }
        for (const clubId of humanClubIds(world)) {
            ensureYouthCoach(world, clubId);
            fillTimeB(world, clubId);
        }
    },
    /**
     * 14 → 15: camisa sem repetir. Quem trocava de clube levava o número do
     * clube antigo e o garoto da base subia sem número; cada elenco fica com um
     * número por jogador, e quem chegou antes guarda o seu.
     */
    14: (world) => {
        arrumarCamisas(world);
    },
};
/**
 * Save que não abriu, dito por chave em vez de por frase.
 *
 * É o único erro do motor que chega inteiro à tela, e chegava em português:
 * quem joga em inglês abria um arquivo corrompido e lia um parágrafo que não
 * entende. A chave e os valores atravessam o worker e viram frase no dicionário
 * de quem está lendo, que é o mesmo caminho da notícia e da recusa.
 *
 * A mensagem do `Error` continua sendo a chave, e não uma frase: é o que
 * aparece num relatório de erro, onde idioma nenhum ajuda e o identificador
 * ajuda.
 */
export class SaveError extends Error {
    key;
    params;
    constructor(key, params = {}) {
        super(key);
        this.name = "SaveError";
        this.key = key;
        this.params = params;
    }
}
export function deserializeWorld(data) {
    const world = (typeof data === "string" ? JSON.parse(data) : data);
    return migrateWorld(world);
}
export function migrateWorld(world) {
    if (typeof world.schemaVersion !== "number") {
        throw new SaveError("save.semEsquema");
    }
    if (world.schemaVersion > SCHEMA_VERSION) {
        throw new SaveError("save.deVersaoMaisNova", { esquema: world.schemaVersion });
    }
    while (world.schemaVersion < SCHEMA_VERSION) {
        const migration = MIGRATIONS[world.schemaVersion];
        if (!migration) {
            throw new SaveError("save.semMigracao", { esquema: world.schemaVersion });
        }
        migration(world);
        world.schemaVersion += 1;
    }
    // O cache de elencos é indexado pela identidade do objeto, então um mundo
    // recém-carregado já nasce sem cache. Mexer nos contadores aqui faria o save
    // divergir do mundo original, quebrando o determinismo.
    world.engineVersion = ENGINE_VERSION;
    return world;
}
export function summarize(world) {
    const humanClubs = [];
    for (const manager of Object.values(world.managers)) {
        if (!manager.human || !manager.clubId)
            continue;
        const club = world.clubs[manager.clubId];
        if (!club)
            continue;
        humanClubs.push({ clubId: club.id, clubName: club.name, managerName: manager.name });
    }
    return {
        seed: world.seed,
        season: world.season,
        round: world.round,
        totalRounds: world.totalRounds,
        phase: world.phase,
        databaseName: world.settings.databaseName,
        schemaVersion: world.schemaVersion,
        engineVersion: world.engineVersion,
        humanClubs,
    };
}
//# sourceMappingURL=serialize.js.map